import { observable, computed, action, reaction, makeObservable } from 'decorators';
import log from 'services/log';
import auth from 'services/auth';
import api from 'services/api';
import { loadTimezones, getCachedTimezone } from 'services/data/timezones';
import moment from 'moment';

export default class TimezonePickerStore {
  @observable _loading = false;
  @observable _loadingDefaults = true;
  @observable _timezoneInfo = null;
  @observable _defaults = [];

  @observable showFullPicker = false;
  @observable country = null;
  @observable loadingTimezones = false;
  @observable countryTimezones = [];
  @observable pickedTimezone = null;

  constructor({ value, notEmpty, detectDefault, countryCode, culture, onChange }) {
    makeObservable(this);
    this._notEmpty = notEmpty;
    this._country = countryCode;
    this._culture = culture;
    this._currentLoadingId = 0;
    this._currentDefaultsLoadingId = 0;
    this._countryLoadingId = 0;
    if (value) {
      this.setNewValue(value);
    }
    this._defaultsDispose = reaction(() => this._calcCountry, () => this._findDefaults(false));
    this._findDefaults(detectDefault && !value, onChange);
  }

  @computed get isLoading() {
    return this._loading || this._loadingDefaults;
  }

  @computed get _calcCountry() {
    return this._country || auth.user?.country;
  }

  @computed get options() {
    const opts = this._defaults.slice();
    if (this._timezoneInfo && !opts.find(o => o.id === this._timezoneInfo.id)) {
      opts.unshift(this._timezoneInfo);
    }
    if (!this._notEmpty) {
      opts.unshift({ id: null, name: null });
    }
    opts.push({ id: -1, name: 'Other...' });
    return opts;
  }

  @action setNewValue(value) {
    if (!value) {
      this._timezoneInfo = null;
      return;
    }
    if (value === this._timezoneInfo?.id) { return; }

    const cached = getCachedTimezone(value);
    if (cached.info) {
      this._timezoneInfo = cached.info;
      return;
    }

    this._loading = true;
    const loadId = this._loadId = this._currentLoadingId++;
    this._timezoneInfo = null;
    cached.promise
      .then(action(info => {
        if (loadId !== this._loadId) { return; }

        this._timezoneInfo = info;
      }))
      .catch(log.catchAndNotify)
      .finally(action(() => (this._loading = false)))
      .done();
  }

  @action onChange(e, onChange) {
    // We picked the special 'other' option
    if (e.id === -1) {
      this.country = null;
      this.countryTimezones = [];
      this.pickedTimezone = null;
      this.showFullPicker = true;
      return;
    }
    this._timezoneInfo = e;
    onChange && onChange(e.id);
  }

  @action closePicker() {
    this.showFullPicker = false;
  }

  @action onChangeCountry(c) {
    this.country = c;
    this._refreshCountryTimezones(c?.code);
  }

  @action selectTimezone(t) {
    this.pickedTimezone = t.id;
  }

  @action setTimezone(onChange) {
    onChange && onChange(this.pickedTimezone);
    this.closePicker();
  }

  searchCountries(q) {
    return api.getAnon(`meta/countries?q=${encodeURIComponent(q)}`)
      .then(res => ({ options: res.data }));
  }

  dispose() {
    this._loadId++;
    this._defaultsLoadId++;
    this._countryLoadId++;
    this._defaultsDispose();
  }

  @action _findDefaults(detectDefault, onChange) {
    this._defaults = [];

    if (!this._calcCountry) {
      this._loadingDefaults = false;
      return;
    }

    this._loadingDefaults = true;
    const loadId = this._defaultsLoadId = this._currentDefaultsLoadingId++;
    loadTimezones({ countryCode: this._calcCountry, culture: this._culture })
      .then(action(info => {
        if (loadId !== this._defaultsLoadId) { return; }

        this._defaults = info;

        if (detectDefault) {
          const current = moment.tz.guess();
          if (current) {
            this.setNewValue(current);
            onChange && onChange(current);
          }
        }

        return null;
      }))
      .catch(log.catchAndNotify)
      .finally(action(() => (this._loadingDefaults = false)))
      .done();
  }

  @action _refreshCountryTimezones(c) {
    this.loadingTimezones = true;
    this.countryTimezones = [];

    if (!c) { return; }

    const loadId = this._countryLoadId = this._countryLoadingId;
    loadTimezones({ countryCode: c, culture: this._culture })
      .then(action(info => {
        if (loadId !== this._countryLoadId) { return; }

        this.countryTimezones = info;
        this.pickedTimezone = info[0]?.id;
      }))
      .catch(log.catchAndNotify)
      .finally(action(() => (this.loadingTimezones = false)))
      .done();
  }
}